import React, { useCallback, useEffect, useRef } from 'react';
import { KoobButton, KoobPlaceholder, KoobTooltip } from '@koob/ui';
import { Formik, FieldArray, Form, useFormikContext } from 'formik';
import { useAsync } from 'react-async';
import { useTranslation } from 'react-i18next';
import { parseBoolean } from 'utils';
import { getHotelsContracts } from 'api/node/contracts';
import { ToggleSection } from 'components';
import KoobTitle from 'components/Koob/KoobTitle';
import { getRoomsDetail } from '../../../../utils/hotelDistribution';
import {
  hasOwnValue,
  resolveDistribution,
  toDistributionPayload,
} from '../../../../utils/distributionInheritance';
import { validateRoomsAllotment } from '../../utils';
import { CLOSED } from '../../../Hotels/Hotel/Contracts/EditContract/seasons/seasonModel';
import EyeButton from './EyeButton';
import DistributionFields from './DistributionFields';
import SeasonDistribution, {
  hasSeveralContracts,
  PeriodDistribution,
} from './SeasonDistribution';
import { Stack } from '@koob/margaret';

// A refused save opens the rooms holding an error: they are collapsed by
// default, so the disabled Save would not say why.
const ExpandRoomsWithErrors = ({ sections }) => {
  const { submitCount, errors } = useFormikContext();

  useEffect(() => {
    if (submitCount === 0) return;
    errors.rooms?.forEach((roomErrors, roomIndex) => {
      if (roomErrors) sections.current[roomIndex]?.expand();
    });
  }, [submitCount, errors, sections]);

  return null;
};

const HotelDetailForm = ({
  hotel,
  onSelectionFinished,
  dmcOrganization,
  toOrganization,
  contracts,
  setHotelAttribute,
}) => {
  const { t } = useTranslation('toConnectionRequest');
  const sections = useRef([]);

  const { data, isLoading } = useAsync({
    promiseFn: getHotelsContracts,
    hotelId: hotel.id,
  });

  // The connection-hotel margin: what pricing applies when no level sets one.
  const hotelMargin = {
    margin: hotel.margin,
    isMarginPercent: hotel.isMarginPercent,
  };

  const canDisplayPeriod = useCallback(
    (period) => {
      if (!contracts) {
        return true;
      }
      const contract = contracts?.find((contract) => {
        return (
          new Date(contract.startAt) <= new Date(period.startAt) &&
          new Date(contract.endAt) >= new Date(period.endAt)
        );
      });
      return !contract?.enabled || contract?.enabled === 'true';
    },
    [contracts],
  );

  // A season follows its contract, as its periods do.
  const canDisplaySeason = useCallback(
    (season) => {
      if (season.category === CLOSED) {
        return false;
      }
      const contract = contracts?.find(
        ({ id }) => String(id) === String(season.contractId),
      );
      return !contract?.enabled || contract?.enabled === 'true';
    },
    [contracts],
  );

  // The room level shows as soon as a season or a period without season does.
  const showsRoomLevel = useCallback(
    (room) =>
      room.seasons.some(canDisplaySeason) ||
      room.periods.some(
        (period) => !period.seasonRoomId && canDisplayPeriod(period),
      ),
    [canDisplayPeriod, canDisplaySeason],
  );

  const validate = useCallback(
    (values) =>
      validateRoomsAllotment(values, {
        canDisplayPeriod,
        canDisplaySeason,
        showsRoomLevel,
        message: t('allotmentNotValid'),
      }),
    [canDisplayPeriod, canDisplaySeason, showsRoomLevel, t],
  );

  const onSubmit = useCallback(
    (values) => {
      // A hidden room keeps its entered values: the API gives its periods no
      // allotment and no free sale until it is shown again.
      const periods = values.rooms.flatMap((room) =>
        room.periods.map((period) => ({
          id: period.id,
          ...toDistributionPayload(period.own),
          isPeriodAllowedForBooking: parseBoolean(room.enabled ?? false),
        })),
      );
      const rooms = values.rooms.map((room) => ({
        id: room.id,
        ...toDistributionPayload(room.distribution),
        seasons: room.seasons
          .filter((season) => season.offered && season.category !== CLOSED)
          .map((season) => ({
            id: season.id,
            ...toDistributionPayload(season.distribution),
          })),
      }));
      setHotelAttribute(hotel.id, 'periods', periods);
      setHotelAttribute(hotel.id, 'rooms', rooms);
      onSelectionFinished();
    },
    [hotel, onSelectionFinished, setHotelAttribute],
  );

  return (
    <>
      {isLoading && (
        <div className="flex-col space-y-5">
          <KoobPlaceholder className="h-20 w-full rounded-md" />
          <KoobPlaceholder className="h-20 w-full rounded-md" />
          <KoobPlaceholder className="h-20 w-full rounded-md" />
        </div>
      )}

      {!isLoading && (
        <>
          <div className="text-center max-w-lg mx-auto">
            <KoobTitle size="text-xl">
              {t('hotelModalTitle', {
                hotelName: hotel.displayName,
                interpolation: { escapeValue: false },
              })}
            </KoobTitle>

            {data?.data.length === 0 && (
              <KoobTitle className="m-4" size="text-sm">
                {t('roomModalInformation')}
              </KoobTitle>
            )}
          </div>

          <Formik
            initialValues={{
              rooms: getRoomsDetail(
                data,
                hotel.periods,
                dmcOrganization,
                toOrganization,
                hotel.rooms,
              ),
            }}
            validate={validate}
            enableReinitialize
            onSubmit={onSubmit}
          >
            {({ values, setFieldValue, isValid, isSubmitting }) => (
              <Form>
                <ExpandRoomsWithErrors sections={sections} />
                {values.rooms.length > 0 && (
                  <p className="mx-5 text-xs text-gray-500">
                    {t('distribution.inheritanceHint')}
                  </p>
                )}
                <FieldArray
                  name="rooms"
                  render={() =>
                    values.rooms.map((room, roomIndex) => {
                      const isDisabled =
                        room.periods.filter(canDisplayPeriod).length === 0;
                      const seasons = room.seasons
                        .map((season, seasonIndex) => ({ season, seasonIndex }))
                        .filter(({ season }) => canDisplaySeason(season));
                      const roomLevels = [room.distribution, hotelMargin];
                      const editedSeasons = seasons.filter(({ season }) =>
                        hasOwnValue(season.distribution),
                      ).length;
                      const periodsWithoutSeason = room.periods
                        .map((period, periodIndex) => ({ period, periodIndex }))
                        .filter(
                          ({ period }) =>
                            !period.seasonRoomId && canDisplayPeriod(period),
                        );
                      const roomLevelLabel =
                        seasons.length > 0
                          ? t('distribution.allSeasons')
                          : t('distribution.allPeriods');
                      const roomFreeSale = room.periods.some(
                        (period) => canDisplayPeriod(period) && period.freeSale,
                      );

                      return (
                        <div
                          className="border m-5 p-2 rounded-md"
                          key={room.id}
                        >
                          <ToggleSection
                            ref={(section) => {
                              sections.current[roomIndex] = section;
                            }}
                            title={room.name}
                            variant="full"
                            disabled={!parseBoolean(room.enabled)}
                            rightElement={
                              <KoobTooltip
                                disabled={!isDisabled}
                                tip={t(
                                  'bulkEdit.editor.periods.periodRequiredForVisibility',
                                )}
                              >
                                <EyeButton
                                  room={room}
                                  roomIndex={roomIndex}
                                  setFieldValue={setFieldValue}
                                  disabled={isDisabled}
                                  className={`${room.name
                                    ?.toLowerCase()
                                    .split(' ')
                                    .join('-')} ${
                                    parseBoolean(room.enabled)
                                      ? 'enabled'
                                      : 'disabled'
                                  }`}
                                />
                              </KoobTooltip>
                            }
                          >
                            <div className="flex flex-col gap-3 py-3">
                              {showsRoomLevel(room) && (
                                <div className="rounded-md bg-gray-50 px-4 py-3">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <span className="text-sm font-semibold text-gray-900">
                                      {roomLevelLabel}
                                    </span>
                                    {editedSeasons > 0 && (
                                      <span className="text-xs text-orange-600">
                                        {t('distribution.seasonsEdited', {
                                          count: editedSeasons,
                                        })}
                                      </span>
                                    )}
                                  </div>
                                  <div className="mt-3">
                                    <DistributionFields
                                      name={`rooms.${roomIndex}.distribution`}
                                      inherited={resolveDistribution(
                                        [hotelMargin],
                                        roomFreeSale,
                                      )}
                                      freeSaleDisabled={!roomFreeSale}
                                      label={`${room.name} · ${roomLevelLabel}`}
                                    />
                                  </div>
                                </div>
                              )}

                              {seasons.map(({ season, seasonIndex }) => (
                                <SeasonDistribution
                                  key={season.id}
                                  room={room}
                                  roomIndex={roomIndex}
                                  season={season}
                                  seasonIndex={seasonIndex}
                                  roomLevels={roomLevels}
                                  canDisplayPeriod={canDisplayPeriod}
                                  withDates={hasSeveralContracts(room.seasons)}
                                />
                              ))}

                              {periodsWithoutSeason.map(
                                ({ period, periodIndex }) => (
                                  <PeriodDistribution
                                    key={period.id}
                                    room={room}
                                    roomIndex={roomIndex}
                                    period={period}
                                    periodIndex={periodIndex}
                                    levels={roomLevels}
                                  />
                                ),
                              )}
                            </div>
                          </ToggleSection>
                        </div>
                      );
                    })
                  }
                />

                <Stack
                  alignX="flex-end"
                  style={{
                    marginTop: '1rem',
                  }}
                  gutterSize={0.5}
                >
                  <KoobButton
                    style={{ marginLeft: 'auto' }}
                    variant="secondary"
                    type="button"
                    onClick={onSelectionFinished}
                  >
                    {t('misc:cancel')}
                  </KoobButton>

                  <KoobButton
                    type="submit"
                    disabled={!isValid}
                    variant="primary"
                    loading={isSubmitting}
                    testId="save-room"
                  >
                    {t('misc:save')}
                  </KoobButton>
                </Stack>
              </Form>
            )}
          </Formik>
        </>
      )}
    </>
  );
};

export default HotelDetailForm;
